import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

export interface UserProfile {
  id: string;
  nome: string;
  telefone: string;
  profissao: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (
    email: string,
    password: string,
    userData: { nome: string; telefone: string; profissao: string }
  ) => Promise<{ error: any; emailDeliveryError?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (userData: { nome: string; telefone: string; profissao: string }) => Promise<{ error: any }>;
  refreshSession: () => Promise<boolean>;
  checkSessionHealth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

// Configurações otimizadas para mobile e desktop
const SESSION_REFRESH_THRESHOLD = 600; // 10 minutos em segundos
const SESSION_CHECK_INTERVAL_DESKTOP = 120000; // 2 minutos em ms
const SESSION_CHECK_INTERVAL_MOBILE = 90000; // 45 segundos em ms (mais frequente para mobile)
const RETRY_ATTEMPTS_LIMIT = 8; // Aumentado para mobile
const MOBILE_REFRESH_TIMEOUT = 45000; // 20 segundos para mobile
const DESKTOP_REFRESH_TIMEOUT = 20000; // 10 segundos para desktop
const NETWORK_RETRY_DELAY = 3000; // 2 segundos de delay entre tentativas de rede

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshInProgress, setRefreshInProgress] = useState<boolean>(false);
  const [retryAttempts, setRetryAttempts] = useState<number>(0);
  const authInitialized = useRef<boolean>(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const sessionCheckIntervalRef = useRef<number | null>(null);
  const isMobile = useIsMobile();
  const sessionCheckInterval = isMobile ? SESSION_CHECK_INTERVAL_MOBILE : SESSION_CHECK_INTERVAL_DESKTOP;
  const visibilityChangeRef = useRef<(() => void) | null>(null);
  const focusEventRef = useRef<(() => void) | null>(null);

  // Fetch profile data
  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for user ID:', userId.substring(0, 5) + '...');
      const { data, error } = await supabase
        .from('perfis')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error.code || error.message);
        return null;
      }

      console.log('Profile fetched successfully');
      return data as UserProfile;
    } catch (error) {
      console.error('Exception fetching profile');
      return null;
    }
  };

  // Check if the session is healthy and needs refresh
  const checkSessionHealth = async (): Promise<boolean> => {
    if (!session) {
      console.log('No session to check health');
      return false;
    }

    try {
      // Check if token is getting close to expiry
      const expiresAt = session.expires_at ? session.expires_at : 0;
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const secondsUntilExpiry = expiresAt - nowInSeconds;
      
      console.log(`Session health check: ${secondsUntilExpiry} seconds until expiry`);
      
      // If token is expiring soon, refresh it proactively
      if (secondsUntilExpiry < SESSION_REFRESH_THRESHOLD) {
        console.log('Session expiring soon, refreshing proactively');
        return await refreshSession();
      }
      
      return true; // Session is healthy
    } catch (error) {
      console.error('Session health check error');
      return false;
    }
  };

  // Detectar conectividade de rede
  const isOnline = () => {
    return navigator.onLine;
  };

  // Esperar pela rede ficar disponível
  const waitForNetwork = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (isOnline()) {
        resolve(true);
        return;
      }

      const handleOnline = () => {
        window.removeEventListener('online', handleOnline);
        resolve(true);
      };

      window.addEventListener('online', handleOnline);
      
      // Timeout após 30 segundos
      setTimeout(() => {
        window.removeEventListener('online', handleOnline);
        resolve(false);
      }, 30000);
    });
  };

  // Attempt to refresh the session token with improved mobile handling
  const refreshSession = async (): Promise<boolean> => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      console.log('Clearing existing refresh timeout');
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (refreshInProgress) {
      console.log('Refresh already in progress, skipping');
      return false;
    }

    try {
      setRefreshInProgress(true);
      console.log('Attempting to refresh session... (mobile:', isMobile, ')');

      // Verificar conectividade antes de tentar
      if (!isOnline()) {
        console.log('Offline detected, waiting for network...');
        const networkAvailable = await waitForNetwork();
        if (!networkAvailable) {
          console.log('Network timeout, aborting refresh');
          return false;
        }
      }

      // Timeout adaptado para mobile
      const refreshTimeout = isMobile ? MOBILE_REFRESH_TIMEOUT : DESKTOP_REFRESH_TIMEOUT;
      
      const refreshPromise = supabase.auth.refreshSession();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Refresh timeout')), refreshTimeout);
      });

      const { data, error } = await Promise.race([refreshPromise, timeoutPromise]);

      if (error) {
        console.error('Error refreshing session:', error.message);
        
        if (error.message.includes('refresh_token_not_found') || 
    error.message.includes('Invalid Refresh Token')) {
  console.error('Refresh token invalid, attempting session recovery before logout:', error);
  const { data: { session: recoveredSession }, error: recoveryError } = await supabase.auth.getSession();
  if (recoveredSession && !recoveryError) {
    console.log('Session recovered successfully');
    setSession(recoveredSession);
    setUser(recoveredSession.user);
    return true;
  } else {
    console.log('Session recovery failed, forcing logout');
    setRetryAttempts(0);
    await handleSignOut(true);
    return false;
  }
}
        
        // Retry logic com backoff exponencial
        if (retryAttempts < RETRY_ATTEMPTS_LIMIT) {
          setRetryAttempts(prev => prev + 1);
          const baseDelay = isMobile ? NETWORK_RETRY_DELAY : 1000;
          const backoffTime = Math.min(baseDelay * Math.pow(2, retryAttempts), 30000);
          
          console.log(`Retry attempt ${retryAttempts + 1}/${RETRY_ATTEMPTS_LIMIT} in ${backoffTime}ms`);
          
          // Toast apenas no último retry em mobile
          if (isMobile && retryAttempts === RETRY_ATTEMPTS_LIMIT - 1) {
            toast.error('Problemas de conectividade', {
              description: 'Tentando reconectar...'
            });
          }
          
          setTimeout(() => {
            refreshSession();
          }, backoffTime);
          return false;
        }
        
        // Falhou em todas as tentativas
        if (error.message.includes('timeout')) {
          toast.error(`Tempo limite excedido (${refreshTimeout/1000}s)`, {
            description: 'Verifique sua conexão'
          });
        } else {
          toast.error('Falha ao atualizar sessão', {
            description: 'Tente fazer login novamente'
          });
        }
        
        // Se estamos em mobile e houve muitos erros, forçar logout
        if (isMobile && retryAttempts >= RETRY_ATTEMPTS_LIMIT) {
          await handleSignOut(true);
        }
        
        return false;
      }
      
      // Reset retry counter on success
      setRetryAttempts(0);

      if (!data.session) {
        console.error('No session returned after refresh');
        return false;
      }

      console.log('Session refreshed successfully, expires:', new Date(data.session.expires_at! * 1000).toISOString());
      
      // Em mobile, mostrar feedback positivo ocasionalmente
      if (isMobile && retryAttempts > 0) {
        toast.success('Conexão restaurada');
      }
      
      setSession(data.session);
      setUser(data.session.user);

      // Fetch profile assíncrono para não bloquear
      setTimeout(() => {
        fetchProfile(data.session.user.id).then(profileData => {
          setProfile(profileData);
        });
      }, 0);

      return true;
    } catch (error: any) {
      console.error('Unexpected error refreshing session:', error.message);
      
      if (error.message.includes('timeout')) {
        toast.error('Tempo limite de conexão excedido', {
          description: 'Verifique sua internet'
        });
      } else {
        toast.error('Erro de conectividade', {
          description: 'Tente fazer login novamente'
        });
      }
      
      return false;
    } finally {
      setRefreshInProgress(false);
    }
  };

  // Handle sign out with improved error handling
  const handleSignOut = async (expired = false) => {
    try {
      console.log('Signing out user, expired session:', expired);
      localStorage.removeItem('dashboardDataTimestamp');

      // Clear session check interval
      if (sessionCheckIntervalRef.current) {
        window.clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }

      // Clean up event listeners
      removeVisibilityAndFocusEventListeners();

      // Clear auth state before API call
      setUser(null);
      setProfile(null);
      setSession(null);
      
      // Set loading to false to prevent infinite loading states
      setLoading(false);

      console.log('Calling supabase.auth.signOut()');
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        console.error('Error during signOut:', error.code || error.message);
        toast.error('Erro ao sair, mas você foi desconectado localmente');
      } else {
        console.log('Supabase signOut completed successfully');
      }

      if (expired) {
        toast.error('Sua sessão expirou. Por favor, faça login novamente.');
      } else {
        toast.success('Você saiu com sucesso');
      }
    } catch (error) {
      console.error('Exception during signOut');
      toast.error('Erro ao sair, tente novamente');
      
      // Ensure user is still logged out locally even if API call fails
      setUser(null);
      setProfile(null);
      setSession(null);
      setLoading(false);
    }
  };

  // Configurar event listeners para visibilidade, foco e conectividade
  const setupVisibilityAndFocusEventListeners = () => {
    // Handler otimizado para quando o app volta a ficar visível (importante no mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('App voltou a ficar visível (mobile focus), verificando sessão');
        // Em mobile, dar um pequeno delay para estabilizar
        setTimeout(() => {
          if (session && user) {
            checkSessionHealth();
          }
        }, isMobile ? 1000 : 100);
      }
    };

    // Handler para quando a janela recupera o foco
    const handleFocus = () => {
      console.log('App recuperou foco, verificando sessão');
      if (session && user) {
        checkSessionHealth();
      }
    };

    // Handler para mudanças de conectividade (importante no mobile)
    const handleOnline = () => {
      console.log('Conectividade restaurada, verificando sessão');
      if (session && user) {
        setTimeout(() => {
          checkSessionHealth();
        }, 1000); // Delay para estabilizar a conexão
      }
    };

    const handleOffline = () => {
      console.log('Conexão perdida - modo offline');
      // Em mobile, mostrar feedback ao usuário
      if (isMobile) {
        toast.error('Conexão perdida', {
          description: 'Algumas funcionalidades podem ficar limitadas'
        });
      }
    };

    // Armazenar referências para remoção posterior
    visibilityChangeRef.current = handleVisibilityChange;
    focusEventRef.current = handleFocus;

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  };

  // Remover event listeners
  const removeVisibilityAndFocusEventListeners = () => {
    if (visibilityChangeRef.current) {
      document.removeEventListener('visibilitychange', visibilityChangeRef.current);
      visibilityChangeRef.current = null;
    }

    if (focusEventRef.current) {
      window.removeEventListener('focus', focusEventRef.current);
      window.removeEventListener('online', focusEventRef.current);
      window.removeEventListener('offline', focusEventRef.current);
      focusEventRef.current = null;
    }
  };

  // Setup periodic session health checks
  useEffect(() => {
    if (session && !sessionCheckIntervalRef.current) {
      console.log(`Setting up periodic session health checks every ${sessionCheckInterval/1000}s`);
      sessionCheckIntervalRef.current = window.setInterval(() => {
        checkSessionHealth().then(healthy => {
          if (!healthy) {
            console.log('Session health check failed, session might be invalid');
          } else {
            console.log('Session health check passed');
          }
        });
      }, sessionCheckInterval);

      // Configurar listeners para eventos de visibilidade e foco
      setupVisibilityAndFocusEventListeners();
    }

    return () => {
      if (sessionCheckIntervalRef.current) {
        console.log('Clearing session health check interval');
        window.clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      removeVisibilityAndFocusEventListeners();
    };
  }, [session, isMobile, sessionCheckInterval]);

  // Update authentication state when session changes
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setLoading(true);
        console.log('Initializing authentication...');

        // First set up the auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, newSession) => {
            console.log('Auth state changed:', event);
            if (!mounted) return;

            if (event === 'SIGNED_OUT' || !newSession) {
              console.log('User signed out or no session');
              setUser(null);
              setProfile(null);
              setSession(null);
              setLoading(false);
              
              // Clear session check interval
              if (sessionCheckIntervalRef.current) {
                window.clearInterval(sessionCheckIntervalRef.current);
                sessionCheckIntervalRef.current = null;
              }
              
              removeVisibilityAndFocusEventListeners();
            } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              console.log('User signed in or token refreshed, expires at:', 
                newSession.expires_at ? new Date(newSession.expires_at * 1000).toISOString() : 'unknown');
              
              setSession(newSession);
              setUser(newSession.user);
              
              // Use setTimeout to avoid potential deadlocks with Supabase
              setTimeout(() => {
                if (mounted) {
                  fetchProfile(newSession.user.id).then(profileData => {
                    if (mounted) {
                      setProfile(profileData);
                      setLoading(false);
                    }
                  });
                }
              }, 0);
            } else {
              // For any other event, ensure loading is set to false
              setLoading(false);
            }
          }
        );

        // Then check for existing session
        console.log('Checking for existing session...');
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting initial session:', error.code || error.message);
          throw error;
        }

        if (!mounted) return;

        if (currentSession) {
          console.log('Existing session found for user, expires at:', 
            currentSession.expires_at ? new Date(currentSession.expires_at * 1000).toISOString() : 'unknown');
          
          setSession(currentSession);
          setUser(currentSession.user);
          const profileData = await fetchProfile(currentSession.user.id);
          setProfile(profileData);
          
          // Check session health immediately to see if we need to refresh
          setTimeout(() => {
            if (mounted) {
              checkSessionHealth();
            }
          }, 0);
        } else {
          console.log('No active session found');
          setUser(null);
          setProfile(null);
          setSession(null);
        }
        
        authInitialized.current = true;
      } catch (error) {
        console.error('Error initializing authentication');
        setUser(null);
        setProfile(null);
        setSession(null);
      } finally {
        if (mounted) {
          console.log('Authentication initialization completed');
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      if (sessionCheckIntervalRef.current) {
        window.clearInterval(sessionCheckIntervalRef.current);
      }
      removeVisibilityAndFocusEventListeners();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      console.log('Attempting sign in');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        console.error('Sign in error:', error.code || error.message);
        toast.error('Email ou senha incorretos');
        return { error };
      }

      console.log('Sign in successful');
      toast.success('Login bem-sucedido');
      return { error: null };
    } catch (error) {
      console.error('Exception during sign in');
      toast.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    userData: { nome: string; telefone: string; profissao: string }
  ) => {
    try {
      console.log('Attempting signup');
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nome: userData.nome,
            telefone: userData.telefone,
            profissao: userData.profissao,
          },
        },
      });

      if (error) {
        if (error.message.includes('already')) {
          console.error('Signup error: Email already in use');
          toast.error('Este email já está em uso');
        } else {
          console.error('Signup error:', error.code || error.message);
          toast.error('Erro ao criar conta');
        }
        return { error };
      }

      if (data?.user && !data?.user?.confirmed_at) {
        console.log('Account created, email confirmation pending');
        if (data?.user?.confirmation_sent_at) {
          console.log('Confirmation email sent');
        } else {
          console.error('No confirmation timestamp, email delivery might have failed');
          return { error: null, emailDeliveryError: true };
        }
      }

      toast.success('Conta criada! Verifique seu email');
      return { error: null };
    } catch (error) {
      console.error('Unexpected error during signup');
      toast.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  // Sign out
  const signOut = async () => {
    console.log('signOut called from component');
    await handleSignOut();
  };

  // Reset password - Função melhorada
  const resetPassword = async (email: string) => {
    try {
      console.log('Requesting password reset');
      
      // Construir a URL de redirecionamento completa com protocolo
      const redirectUrl = `${window.location.origin}/reset-password-confirm`;
      console.log('Using redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Password reset error:', error.code || error.message);
        toast.error('Erro ao enviar email de recuperação');
        return { error };
      }

      console.log('Password reset email sent successfully');
      toast.success('Email de recuperação enviado');
      return { error: null };
    } catch (error) {
      console.error('Error in password reset:', error);
      toast.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  // Update user profile
  const updateProfile = async (userData: { nome: string; telefone: string; profissao: string }) => {
    try {
      if (!user) {
        return { error: new Error('Usuário não autenticado') };
      }

      console.log('Updating user metadata');
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          nome: userData.nome,
          telefone: userData.telefone,
          profissao: userData.profissao,
        },
      });

      if (metadataError) {
        console.error('Error updating auth metadata:', metadataError.code || metadataError.message);
        toast.error('Erro ao atualizar dados de autenticação');
        return { error: metadataError };
      }

      console.log('Updating profile in database');
      const { error: profileError } = await supabase
        .from('perfis')
        .update({
          nome: userData.nome,
          telefone: userData.telefone,
          profissao: userData.profissao,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError.code || profileError.message);
        toast.error('Erro ao atualizar perfil');
        return { error: profileError };
      }

      setProfile({
        ...profile as UserProfile,
        nome: userData.nome,
        telefone: userData.telefone,
        profissao: userData.profissao,
      });

      console.log('Profile updated successfully');
      toast.success('Perfil atualizado com sucesso');
      return { error: null };
    } catch (error) {
      console.error('Exception updating profile');
      toast.error('Ocorreu um erro, tente novamente');
      return { error };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    refreshSession,
    checkSessionHealth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
